import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Upload, Download, Loader2, FileText, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const MODES = [
  { value: "regular", label: "Greige", desc: "Regular Dhoti — full weaving spec + processing + trims" },
  { value: "tissue", label: "Tissue", desc: "Tissue Dhoti — adds Weft 2 count/quality" },
  { value: "fob", label: "FOB", desc: "Product cost + discount + trims + border" },
  { value: "shirt", label: "Shirt", desc: "Fabric rate × consumption + CMT" },
  { value: "combo", label: "Combo", desc: "Bundle 2–4 existing designs (referenced by design_name)" },
];

function ImportPanel({ mode, label, desc }) {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const downloadTemplate = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/saved-designs/bulk-import/template?mode=${mode}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `bulk_import_${mode}_template.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setDownloading(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post(`/saved-designs/bulk-import?mode=${mode}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 600000,
      });
      setResult(res.data);
      const r = res.data;
      const created = r.imported || 0;
      const updated = r.updated || 0;
      if (r.failed > 0) {
        toast.warning(`Created ${created}, updated ${updated} of ${r.total} — ${r.failed} failed`);
      } else {
        toast.success(`Created ${created}, updated ${updated} (${r.tagged} auto-tagged)`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-5" data-testid={`bulk-import-panel-${mode}`}>
      <div className="bg-stone-50 border border-stone-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <FileText className="h-5 w-5 text-[#C2410C] mt-0.5 shrink-0" />
          <div>
            <h3 className="font-heading font-semibold text-stone-900">{label}</h3>
            <p className="text-xs text-stone-500 mt-1">{desc}</p>
            <p className="text-xs text-stone-500 mt-2">
              <span className="font-semibold">Workflow:</span> Each row builds the form, runs cost calculation, saves the design, and (if <code className="bg-white px-1 py-0.5 rounded text-[10px]">tagged_barcode</code> is set) auto-tags + auto-fills trims/WSP/MRP from masters.
            </p>
            <p className="text-xs text-stone-500 mt-2">
              <span className="font-semibold">Re-import:</span> If a row's <code className="bg-white px-1 py-0.5 rounded text-[10px]">design_name</code> already exists, the existing record is <span className="font-semibold">updated</span> in place — the previous values are snapshotted to history first. Tagged_barcode and tagged_item from earlier are preserved.
            </p>
            <p className="text-xs text-stone-500 mt-2">
              <span className="font-semibold">Multiple vendors:</span> Use semicolon (<code className="bg-white px-1 py-0.5 rounded text-[10px]">;</code>) to separate vendor names — not comma, since vendor names often contain commas. Example: <code className="bg-white px-1 py-0.5 rounded text-[10px]">A.R.S. Textiles, Omalur; Giri Fabrics, Vagarayampalayam</code>.
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <Button
          data-testid={`download-template-${mode}`}
          variant="outline"
          onClick={downloadTemplate}
          disabled={downloading}
          className="border-stone-300"
        >
          {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
          Download Template
        </Button>

        <label className="inline-block">
          <Input
            data-testid={`upload-csv-${mode}`}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={handleUpload}
            disabled={uploading}
          />
          <Button
            asChild
            className="bg-[#C2410C] hover:bg-[#9A3412] text-white"
            disabled={uploading}
          >
            <span>
              {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              {uploading ? "Importing..." : "Upload CSV"}
            </span>
          </Button>
        </label>
      </div>

      {result && (
        <div className="border border-stone-200 rounded-lg overflow-hidden" data-testid={`import-result-${mode}`}>
          <div className="bg-stone-50 px-4 py-3 border-b border-stone-200 grid grid-cols-2 sm:grid-cols-5 gap-3">
            <Stat label="Total rows" value={result.total} />
            <Stat label="Created" value={result.imported} positive={result.imported > 0} />
            <Stat label="Updated" value={result.updated || 0} positive={(result.updated || 0) > 0} />
            <Stat label="Auto-tagged" value={result.tagged} positive={result.tagged > 0} />
            <Stat label="Failed" value={result.failed} negative={result.failed > 0} />
          </div>
          {result.errors?.length > 0 && (
            <div className="p-4">
              <div className="flex items-center gap-2 text-xs font-semibold text-stone-700 mb-2 uppercase tracking-wider">
                <AlertCircle className="h-3.5 w-3.5 text-red-500" />
                Errors ({result.errors.length})
              </div>
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {result.errors.map((e, idx) => (
                  <div key={idx} className="text-sm border-l-2 border-red-200 bg-red-50 px-3 py-1.5 rounded">
                    <span className="text-stone-500 text-xs">Row {e.row}:</span>{" "}
                    <span className="font-medium text-stone-800">{e.name || "(no name)"}</span>{" "}
                    <span className="text-red-700">— {e.error}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {result.errors?.length === 0 && (result.imported > 0 || (result.updated || 0) > 0) && (
            <div className="px-4 py-3 text-sm text-green-700 bg-green-50 flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              All rows processed successfully. Visit Saved Designs to view.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, positive, negative }) {
  const color = negative ? "text-red-600" : positive ? "text-green-700" : "text-stone-900";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">{label}</div>
      <div className={`text-2xl font-bold font-mono ${color}`}>{value ?? 0}</div>
    </div>
  );
}

export default function BulkImportPage() {
  const [tab, setTab] = useState("regular");
  return (
    <div className="space-y-6" data-testid="bulk-import-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-stone-900 tracking-tight">
          Bulk Import Designs
        </h1>
        <p className="text-sm text-stone-500 mt-1">
          Import many designs at once from a CSV. Download the template for each mode, fill in your parameters, and upload. The system calculates cost, saves under the given name, and auto-tags barcodes when provided.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-stone-100">
          {MODES.map((m) => (
            <TabsTrigger key={m.value} value={m.value} data-testid={`bulk-tab-${m.value}`}>{m.label}</TabsTrigger>
          ))}
        </TabsList>
        {MODES.map((m) => (
          <TabsContent key={m.value} value={m.value} className="mt-5">
            <ImportPanel mode={m.value} label={m.label} desc={m.desc} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
